import {
  Autocomplete,
  AutocompleteRenderInputParams,
  BaseTextFieldProps,
  TextField,
} from '@mui/material';
import {useEffect, useMemo} from 'react';
// import {Apartment} from 'src/apis/autoGenerate';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {useAppSelector} from 'src/redux/hooks';
import {getApartmentList, selectProperty} from 'src/redux/slice/propertySlice';
import {selectAccount} from 'src/redux/slice/accountSlice';

export interface AutocompleteOption {
  label: string | undefined;
  value: any;
}

export type AutocompleteVal = AutocompleteOption | null;

interface SearchApartmentsProps extends Partial<AutocompleteRenderInputParams> {
  onChange: (value: Apartment | null) => void;
  /** Omit for uncontrolled. Pass `null` or an apartment for controlled selection. */
  value?: Apartment | null;
  textFieldProps?: BaseTextFieldProps;
  /** Used as the TextField `label` (floating label). */
  placeholder?: string;
  /** Optional controlled dropdown (e.g. open picker from a CTA button). */
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export default function SearchApartments({
  onChange,
  value: valueProp,
  textFieldProps,
  placeholder,
  open,
  onOpen,
  onClose,
  ...otherProps
}: SearchApartmentsProps) {
  const property = useAppSelector(selectProperty);
  const account = useAppSelector(selectAccount);
  useEffect(() => {
    const orgId = account.role?.org_id;
    const userId = account.userId;

    if (orgId) {
      getApartmentList(orgId, userId); // Pass userId to ensure correct apartment permissions
    } else {
      console.warn('Skipping getApartmentList: Missing orgId and userId');
    }
  }, [account.role, account.userId]); // Only run when account updates

  const handleSelect = (_e: unknown, val: (Apartment & {label?: string}) | null) => {
    onChange(val);
  };

  const options = useMemo(() => {
    return property.apartmentList.length === 0
      ? []
      : property.apartmentList.map(apartment => {
          return {
            label: apartment.name,
            ...apartment,
          };
        });
  }, [property.apartmentList]);

  const isControlled = valueProp !== undefined;
  const resolvedValue = useMemo(() => {
    if (!isControlled) {
      return undefined;
    }
    if (valueProp === null) {
      return null;
    }
    return options.find(o => o.id === valueProp.id) ?? valueProp;
  }, [isControlled, valueProp, options]);

  return (
    <Autocomplete
      size="small"
      id="combo-box-demo"
      options={options}
      sx={{width: otherProps.fullWidth ? '100%' : 300}}
      {...(isControlled ? {value: resolvedValue} : {})}
      onChange={handleSelect}
      isOptionEqualToValue={(a, b) => Boolean(a?.id && b?.id && a.id === b.id)}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      renderInput={params => (
        <TextField {...params} label={placeholder || 'Search property'} {...textFieldProps} />
      )}
      {...otherProps}
    />
  );
}
